import {
	isTrackCaptionKind,
	useMediaState,
	CaptionButton as VideoCaptionButton,
	FullscreenButton as VideoFullscreenButton,
	MuteButton as VideoMuteButton,
	PIPButton as VideoPIPButton,
	PlayButton as VideoPlayButton,
} from '@vidstack/react'
import {
	Minimize as FullscreenExitIcon,
	Maximize as FullscreenIcon,
	VolumeX as MuteIcon,
	PauseIcon,
	PictureInPictureIcon as PictureInPictureExitIcon,
	PictureInPicture2 as PictureInPictureIcon,
	PlayIcon,
	SubtitlesIcon,
	Volume2 as VolumeHighIcon,
	Volume1 as VolumeLowIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

export function PlayButton() {
	const { t } = useTranslation()

	const isPaused = useMediaState('paused')

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button asChild variant='ghost' size='icon'>
					<VideoPlayButton>
						{isPaused ? (
							<PlayIcon className='h-7 w-7 translate-x-px' />
						) : (
							<PauseIcon className='h-7 w-7' />
						)}
					</VideoPlayButton>
				</Button>
			</TooltipTrigger>
			<TooltipContent>
				{isPaused
					? t('player.play', { defaultValue: 'Play' })
					: t('player.pause', { defaultValue: 'Pause' })}
			</TooltipContent>
		</Tooltip>
	)
}

export function MuteButton() {
	const { t } = useTranslation()

	const volume = useMediaState('volume'),
		isMuted = useMediaState('muted')

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button asChild variant='ghost' size='icon'>
					<VideoMuteButton>
						{isMuted || volume == 0 ? (
							<MuteIcon className='h-7 w-7' />
						) : volume < 0.5 ? (
							<VolumeLowIcon className='h-7 w-7' />
						) : (
							<VolumeHighIcon className='h-7 w-7' />
						)}
					</VideoMuteButton>
				</Button>
			</TooltipTrigger>
			<TooltipContent>
				{isMuted
					? t('player.unmute', { defaultValue: 'Unmute' })
					: t('player.mute', { defaultValue: 'Mute' })}
			</TooltipContent>
		</Tooltip>
	)
}

export function CaptionButton() {
	const { t } = useTranslation()

	const track = useMediaState('textTrack'),
		isOn = track && isTrackCaptionKind(track)

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button asChild variant='ghost' size='icon'>
					<VideoCaptionButton>
						<SubtitlesIcon
							className={`h-7 w-7 ${!isOn ? 'text-white/60' : ''}`}
						/>
					</VideoCaptionButton>
				</Button>
			</TooltipTrigger>
			<TooltipContent>
				{isOn
					? t('player.closed_captions_off', {
							defaultValue: 'Closed Captions Off',
						})
					: t('player.closed_captions_on', {
							defaultValue: 'Closed Captions On',
						})}
			</TooltipContent>
		</Tooltip>
	)
}

export function PIPButton() {
	const { t } = useTranslation()

	const isActive = useMediaState('pictureInPicture')

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button asChild variant='ghost' size='icon'>
					<VideoPIPButton>
						{isActive ? (
							<PictureInPictureExitIcon className='h-7 w-7' />
						) : (
							<PictureInPictureIcon className='h-7 w-7' />
						)}
					</VideoPIPButton>
				</Button>
			</TooltipTrigger>
			<TooltipContent>
				{isActive
					? t('player.exit_pip', { defaultValue: 'Exit PIP' })
					: t('player.enter_pip', { defaultValue: 'Enter PIP' })}
			</TooltipContent>
		</Tooltip>
	)
}

export function FullscreenButton() {
	const { t } = useTranslation()

	const isActive = useMediaState('fullscreen')

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button asChild variant='ghost' size='icon'>
					<VideoFullscreenButton>
						{isActive ? (
							<FullscreenExitIcon className='h-7 w-7' />
						) : (
							<FullscreenIcon className='h-7 w-7' />
						)}
					</VideoFullscreenButton>
				</Button>
			</TooltipTrigger>
			<TooltipContent>
				{isActive
					? t('player.exit_fullscreen', { defaultValue: 'Exit Fullscreen' })
					: t('player.enter_fullscreen', { defaultValue: 'Enter Fullscreen' })}
			</TooltipContent>
		</Tooltip>
	)
}
